import React from 'react';
import { 
  Box, 
  Stack, 
  Typography, 
  Checkbox, 
  Card,
  CardContent,
  Sheet,
  Divider,
  Chip,
  Input,
  Switch,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  Option,
  IconButton,
  ModalClose,
} from '@mui/joy';
import { FaHistory, FaTruck, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import type { ProjectEvent } from './EventManager';
import SupplierSelector from './SupplierSelector';

import type { Database } from '../types/supabase';
import { directSupabase } from '../lib/supabaseClient';
import { PRODUCT_TYPE_OPTIONS } from '../pages/project-workspace/WorkspaceProductTypesCard';



type Supplier = Database['public']['Tables']['suppliers']['Row'];

// Define production date entry type
export interface ProductionDateEntry {
  id: string;
  productType: string;
  supplierId: string;
  date: string;
  paid?: 'Deposit' | 'Fully' | null;
  /** The supplier's own order reference for this line. */
  reference?: string | null;
  /** Where the supplier delivers this line. */
  deliveryTo?: 'yard' | 'site' | null;
  /** Provisional until the supplier confirms; anything else counts as a fixed date. */
  dateStatus?: 'fixed' | 'provisional' | null;
}

/** Paid status labels; no value means None. */
export const PRODUCTION_PAID_LABELS: Record<'Deposit' | 'Fully', string> = {
  Deposit: 'Deposit',
  Fully: 'Fully paid',
};

export const DELIVERY_TO_LABELS: Record<'yard' | 'site', string> = {
  yard: 'Yard address',
  site: 'Site address',
};

// Define workflow task type
export interface WorkflowTask {
  task: string;
  completed: boolean;
  date?: string;
  productionDates?: ProductionDateEntry[];
}

// Define stage names as a union type
export type WorkflowStage = 
  | 'New'
  | 'Supplier Quote Received'
  | 'Deposit Received'
  | 'Ordered'
  | 'Installation Scheduled'
  | 'Delivery Scheduled'
  | 'Cancelled'
  | 'Completed';

// Define stages with their associated tasks for standard workflow
export const WORKFLOW_STAGES: Record<WorkflowStage, readonly string[]> = {
  'New': [
    'First Survey Complete',
  ] as const,
  'Supplier Quote Received': [
    'Supplier Quote Received',
  ] as const,
  'Deposit Received': [
    'Deposit received',
    'Final Survey Complete',
    'Order Goods'
  ] as const,
  'Ordered': [
    'Schedule Installation Date'
  ] as const,
  'Installation Scheduled': [
    'Installation Complete',
    'Request payment',
    'Receive payment',
    'Send Receipt',
    'Request Review'
  ] as const,
  'Delivery Scheduled': [
    'Delivery Complete',
    'Request payment',
    'Receive payment',
    'Send Receipt',
    'Request Review'
  ] as const,
  'Cancelled': [] as const,  // No tasks - projects become cancelled when "Cancel Project" is checked in Completed section
  'Completed': [
    'Complete Project',
    'Cancel Project'
  ] as const
};

// Define stages with their associated tasks for supply-only workflow
export const SUPPLY_ONLY_WORKFLOW_STAGES: Record<WorkflowStage, readonly string[]> = {
  'New': [
    'First Survey Complete',
  ] as const,
  'Supplier Quote Received': [
    'Supplier Quote Received',
  ] as const,
  'Deposit Received': [
    'Deposit received',
    'Final Survey Complete',
    'Order Goods'
  ] as const,
  'Ordered': [
    'Schedule Delivery Date'
  ] as const,
  'Installation Scheduled': [
    'Installation Complete',
    'Request payment',
    'Receive payment',
    'Send Receipt',
    'Request Review'
  ] as const,
  'Delivery Scheduled': [
    'Delivery Complete',
    'Request payment',
    'Receive payment',
    'Send Receipt',
    'Request Review'
  ] as const,
  'Cancelled': [] as const,  // No tasks - projects become cancelled when "Cancel Project" is checked in Completed section
  'Completed': [
    'Complete Project',
    'Cancel Project'
  ] as const
};

// Array of stages in order
export const PROGRESS_STAGES = [
  'New',
  'Supplier Quote Received',
  'Deposit Received',
  'Ordered',
  'Installation Scheduled',
  'Completed'
] as const;

// Array of stages in order for supply-only workflow
export const SUPPLY_ONLY_PROGRESS_STAGES = [
  'New',
  'Supplier Quote Received',
  'Deposit Received',
  'Ordered',
  'Delivery Scheduled',
  'Completed'
] as const;

// Define the desired event order for workflow
const WORKFLOW_EVENT_ORDER = [
  'installationDateConfirmation',
  'deliveryDateConfirmation',
  'finalSurveyAppointmentConfirmation',
  'finalSurveyAppointmentRequest',
  'firstSurveyAppointmentConfirmation',
  'firstSurveyAppointmentRequest',
  // (Add more event types as needed)
];

// Props for ProjectWorkflow component
interface ProjectWorkflowProps {
  tasks?: WorkflowTask[];
  onTaskChange: (taskName: string, completed: boolean) => void;
  readOnly?: boolean;
  condensed?: boolean;
  workflowTasks?: WorkflowTask[];
  onTasksChange?: (tasks: WorkflowTask[]) => void;
  project?: any;
  onSendFirstSurveyAppointmentRequest?: () => void;
  onSendFinalSurveyAppointmentRequest?: () => void;
  onProjectUpdated?: () => void;
  onEnsureProjectSaved?: () => Promise<boolean>;
  onProjectCompleteChange?: (completed: boolean) => void;
  events?: ProjectEvent[];
  supplyOnly?: boolean;
  onSupplyOnlyChange?: (supplyOnly: boolean) => void;
}

// Map workflow tasks to related project event types
const TASK_EVENT_TYPE_MAP: Record<string, string> = {
  'First Survey Complete': 'firstSurveyAppointmentConfirmation',
  'Final Survey Complete': 'finalSurveyAppointmentConfirmation',
  'Schedule Installation Date': 'installationDateConfirmation',
  'Schedule Delivery Date': 'deliveryDateConfirmation',
};

export function supplierCategoryForProduct(productType: string): 'Glass' | 'Windows' | undefined {
  const key = productType.trim().toLowerCase();
  if (key === 'glass') return 'Glass';
  if (key === 'window' || key === 'windows') return 'Windows';
  return undefined;
}

export function productionProductOptions(_workspaceTypes: string[] | undefined, current?: string): string[] {
  const base: string[] = [...PRODUCT_TYPE_OPTIONS];
  if (current && !base.includes(current)) return [current, ...base];
  return base;
}

// Production Date Manager Component
interface ProductionDateManagerProps {
  productionDates: ProductionDateEntry[];
  onProductionDatesChange: (dates: ProductionDateEntry[]) => void;
  productTypes?: string[] | null;
  readOnly?: boolean;
}

export const ProductionDateManager: React.FC<ProductionDateManagerProps> = ({
  productionDates = [],
  onProductionDatesChange,
  productTypes,
  readOnly = false,
}) => {
  const [suppliersList, setSuppliersList] = React.useState<Supplier[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Load suppliers
  React.useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setLoading(true);
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) throw error;
        setSuppliersList(data || []);
      } catch (err) {
        console.error('Error loading suppliers:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSuppliers();
  }, []);

  const addProductionDate = () => {
    const selected = (productTypes || []).map((type) => String(type).trim()).filter(Boolean);
    const options = productionProductOptions(productTypes || undefined);
    const newEntry: ProductionDateEntry = {
      id: Date.now().toString(),
      productType: selected[0] || options[0] || 'Glass',
      supplierId: '',
      date: '',
      paid: null,
    };
    onProductionDatesChange([...productionDates, newEntry]);
  };

  const removeProductionDate = (id: string) => {
    onProductionDatesChange(productionDates.filter(entry => entry.id !== id));
  };

  const updateProductionDate = (id: string, field: keyof ProductionDateEntry, value: string | null) => {
    onProductionDatesChange(
      productionDates.map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  // Check if all entries have valid data
  const allEntriesValid = productionDates.every(entry => entry.supplierId && entry.date);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', overflow: 'visible' }}>
      {productionDates.map((entry) => (
        <Box
          key={entry.id}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' },
            alignItems: { xs: 'stretch', sm: 'center', md: 'center' },
            gap: { xs: 0.75, sm: 0.75, md: 1 },
            p: { xs: 0.75, sm: 0.75, md: 1 },
            px: { xs: 0.75, sm: 0.75, md: 1 },
            border: '1px solid',
            borderColor: entry.supplierId && entry.date ? 'success.300' : 'divider',
            borderRadius: 'sm',
            backgroundColor: entry.supplierId && entry.date ? 'success.50' : 'background.level1',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'visible',
          }}
        >
          <Select
            size="sm"
            value={entry.productType}
            onChange={(_, value) => updateProductionDate(entry.id, 'productType', value || productionProductOptions(productTypes || undefined, entry.productType)[0] || 'Glass')}
            disabled={readOnly}
            sx={{ 
              width: { xs: '100%', sm: 'auto', md: 150 },
              minWidth: { xs: '100%', sm: 130, md: 140 },
              maxWidth: { xs: '100%', sm: 180, md: 180 },
              flexShrink: 0,
            }}
          >
            {productionProductOptions(productTypes || undefined, entry.productType).map((type) => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
          
          <Box sx={{ 
            flex: { xs: 'none', sm: '1 1 auto', md: '1 1 0%' },
            minWidth: { xs: '100%', sm: '200px', md: 0 },
            maxWidth: { xs: '100%', sm: '100%', md: 'none' },
            width: { xs: '100%', sm: 'auto', md: 'auto' },
          }}>
            <SupplierSelector
              value={entry.supplierId}
              onChange={(id: string) => updateProductionDate(entry.id, 'supplierId', id)}
              supplierType={supplierCategoryForProduct(entry.productType)}
            />
          </Box>
          
          <Input
            type="date"
            size="sm"
            value={entry.date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateProductionDate(entry.id, 'date', e.target.value)}
            disabled={readOnly}
            sx={{ 
              width: { xs: '100%', sm: 'auto', md: 135 },
              minWidth: { xs: '100%', sm: 125, md: 135 },
              maxWidth: { xs: '100%', sm: 165, md: 165 },
              flexShrink: 0,
            }}
            error={!entry.date && productionDates.length > 0}
          />
          
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.75,
              flexBasis: { xs: '100%', sm: '100%', md: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-start', md: 'flex-start' },
            }}
          >
            <Box
              sx={{
                width: { xs: '45%', sm: 120, md: 95 },
                minWidth: { xs: '35%', sm: 90, md: 80 },
                maxWidth: { xs: '55%', sm: 150, md: 115 },
                flexShrink: 0,
              }}
            >
              <Select
                size="sm"
                value={entry.paid ?? ''}
                onChange={(_, value) => updateProductionDate(entry.id, 'paid', value === '' ? null : (value || null))}
                disabled={readOnly}
                placeholder="Paid?"
                sx={{ width: '100%' }}
              >
                <Option value="">None</Option>
                <Option value="Deposit">{PRODUCTION_PAID_LABELS.Deposit}</Option>
                <Option value="Fully">{PRODUCTION_PAID_LABELS.Fully}</Option>
              </Select>
            </Box>
            
            {!readOnly && (
              <IconButton
                size="sm"
                color="danger"
                variant="plain"
                onClick={() => removeProductionDate(entry.id)}
                sx={{
                  flexShrink: 0,
                }}
              >
                <IconWrapper icon={FaTrash} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
      
      {!readOnly && (
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<IconWrapper icon={FaPlus} />}
          onClick={addProductionDate}
          sx={{ 
            alignSelf: { xs: 'stretch', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Add Production Date
        </Button>
      )}
      
      {productionDates.length > 0 && !allEntriesValid && (
        <Typography level="body-xs" color="warning" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          Fill in a supplier and date for each row.
        </Typography>
      )}
    </Box>
  );
};

// Helper function to determine stage based on task completion
const getStageFromTasks = (tasks: WorkflowTask[], project?: any, supplyOnly?: boolean): WorkflowStage => {
  // Check if project is cancelled (using project_status or cancel task)
  if (project && project.project_status === 'cancelled') {
    return 'Cancelled';
  }
  const cancelTask = tasks.find(t => t.task === 'Cancel Project');
  if (cancelTask?.completed) {
    return 'Cancelled';
  }
  
  // If there are no tasks or all tasks are incomplete, return 'New'
  if (!tasks || tasks.length === 0 || tasks.every(t => !t.completed)) {
    return 'New';
  }
  
  // Return 'Completed' if the 'Complete Project' workflow task is checked OR if project.complete is true OR if project_status is completed
  const completeTask = tasks.find(t => t.task === 'Complete Project');
  if (completeTask?.completed || (project && project.complete) || (project && project.project_status === 'completed')) {
    return 'Completed';
  }
  
  const stages = supplyOnly ? SUPPLY_ONLY_PROGRESS_STAGES : PROGRESS_STAGES;
  const workflowStages = supplyOnly ? SUPPLY_ONLY_WORKFLOW_STAGES : WORKFLOW_STAGES;
  
  // If all tasks in Installation/Delivery Scheduled are completed, but Complete Project is not, stay at that stage
  const scheduledStage = supplyOnly ? 'Delivery Scheduled' : 'Installation Scheduled';
  const scheduledTasks = workflowStages[scheduledStage];
  const scheduledStageTasks = tasks.filter(t => scheduledTasks.includes(t.task));
  if (scheduledStageTasks.length > 0 && scheduledStageTasks.every(t => t.completed)) {
    return scheduledStage;
  }
  
  // For each stage in order, check if all tasks in that stage are completed
  for (let i = 0; i < stages.length; i++) {
    const stage = stages[i];
    const stageTaskNames = workflowStages[stage];
    const stageTasks = tasks.filter(t => stageTaskNames.includes(t.task));
    if (stageTasks.length === 0) continue; // skip if no tasks for this stage
    const allCompleted = stageTasks.every(t => t.completed);
    if (!allCompleted) {
      return stage;
    }
  }
  
  // If all stages' tasks are completed, return last stage
  return stages[stages.length - 1];
};

const ProjectWorkflow: React.FC<ProjectWorkflowProps> = ({
  tasks = [],
  workflowTasks,
  onTaskChange,
  onTasksChange,
  readOnly = false,
  condensed = false,
  project,
  onSendFirstSurveyAppointmentRequest,
  onSendFinalSurveyAppointmentRequest,
  onProjectUpdated,
  onEnsureProjectSaved,
  onProjectCompleteChange,
  events = [],
  supplyOnly = false,
  onSupplyOnlyChange,
}) => {
  // Use either tasks or workflowTasks
  const effectiveTasks = tasks.length > 0 ? tasks : (workflowTasks || []);
  
  // Compute current stage based on task completion and project status
  const currentStage = React.useMemo(() => getStageFromTasks(effectiveTasks, project, supplyOnly), [effectiveTasks, project, supplyOnly]);

  // State for supply-only toggle
  const [showToggleWarning, setShowToggleWarning] = React.useState(false);
  const [pendingSupplyOnlyChange, setPendingSupplyOnlyChange] = React.useState(false);
  
  // State for editing production dates
  const [editingProductionDates, setEditingProductionDates] = React.useState(false);
  
  // State for suppliers list
  const [suppliersList, setSuppliersList] = React.useState<Supplier[]>([]);

  // Load suppliers
  React.useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) throw error;
        setSuppliersList(data || []);
      } catch (err) {
        console.error('Error loading suppliers:', err);
      }
    };
    loadSuppliers();
  }, []);

  // Get supplier name by ID
  const getSupplierName = (supplierId: string): string => {
    const supplier = suppliersList.find(s => s.id === supplierId);
    return supplier?.supplier_name || supplierId;
  };

  // Group tasks by stage
  const tasksByStage = React.useMemo(() => {
    const stages = supplyOnly ? SUPPLY_ONLY_PROGRESS_STAGES : PROGRESS_STAGES;
    const workflowStages = supplyOnly ? SUPPLY_ONLY_WORKFLOW_STAGES : WORKFLOW_STAGES;
    
    return (stages as readonly string[]).reduce((acc: Record<string, WorkflowTask[]>, stage: string) => {
      const stageTaskNames = workflowStages[stage as WorkflowStage];
      const stageTasks = effectiveTasks.filter(task => stageTaskNames.includes(task.task));
      acc[stage] = stageTasks;
      return acc;
    }, {} as Record<string, WorkflowTask[]>);
  }, [effectiveTasks, supplyOnly]);

  // Sort events according to workflow order
  const sortedEvents = React.useMemo(() => {
    return [...events].sort((a, b) => {
      const aIdx = WORKFLOW_EVENT_ORDER.indexOf(a.event_type);
      const bIdx = WORKFLOW_EVENT_ORDER.indexOf(b.event_type);
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }, [events]);

  const handleDateChange = (taskName: string, date: string) => {
    const updatedTasks = effectiveTasks.map(task => 
      task.task === taskName ? { ...task, date } : task
    );
    onTasksChange?.(updatedTasks);
  };

  const handleProductionDatesChange = (taskName: string, productionDates: ProductionDateEntry[]) => {
    updateProductionDates(taskName, productionDates);
  };

  const updateProductionDates = (taskName: string, productionDates: ProductionDateEntry[]) => {
    const updatedTasks = effectiveTasks.map(task => 
      task.task === taskName ? { ...task, productionDates } : task
    );
    
    // Auto-complete the task if there are production dates with valid dates
    const task = updatedTasks.find(t => t.task === taskName);
    if (task && taskName === 'Production Date') {
      const hasValidDates = productionDates.some(entry => entry.date && entry.supplierId);
      if (hasValidDates && !task.completed) {
        onTaskChange(taskName, true);
      } else if (!hasValidDates && task.completed) {
        onTaskChange(taskName, false);
      }
    }
    
    onTasksChange?.(updatedTasks);
  };

  const handleSupplyOnlyToggle = (checked: boolean) => {
    if (checked !== supplyOnly) {
      setPendingSupplyOnlyChange(checked);
      setShowToggleWarning(true);
    }
  };

  const confirmSupplyOnlyChange = () => {
    onSupplyOnlyChange?.(pendingSupplyOnlyChange);
    setShowToggleWarning(false);
    setPendingSupplyOnlyChange(false);
  };

  const cancelSupplyOnlyChange = () => {
    setShowToggleWarning(false);
    setPendingSupplyOnlyChange(false);
  };

  const stages = supplyOnly ? SUPPLY_ONLY_PROGRESS_STAGES : PROGRESS_STAGES;

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: condensed ? 0 : 3,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          borderRadius: 'md',
          backgroundColor: '#fff',
          boxShadow: 'sm',
          width: '100%',
        }}
      >
        <CardContent sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="title-lg" startDecorator={<IconWrapper icon={FaHistory} />}>
              Project Tasks
            </Typography>
            <Chip size="sm" variant="soft" color="primary">
              {currentStage}
            </Chip>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {readOnly ? (
            supplyOnly && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Typography level="body-md" fontWeight="bold">SUPPLY ONLY</Typography>
              </Box>
            )
          ) : (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <FormControl size="sm">
                <FormLabel>Supply Only</FormLabel>
                <Switch
                  checked={supplyOnly}
                  onChange={(e) => handleSupplyOnlyToggle(e.target.checked)}
                />
              </FormControl>
            </Box>
          )}
        </CardContent>
        <CardContent sx={{ pt: 0, pb: 3, px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Stack spacing={2}>
            {stages.map((stage) => {
              const stageTasks = tasksByStage[stage] || [];
              let completedCount = stageTasks.filter((t: WorkflowTask) => t.completed).length;
              let totalCount = stageTasks.length;
              if (stage === 'Completed') {
                // Don't override the count if both Complete and Cancel tasks exist
                // Let the natural count show (e.g., 0/2 means no tasks completed, 1/2 means one is completed, etc.)
              }
                
              return (
                <Sheet
                  key={stage}
                  variant="outlined"
                  sx={{
                    borderRadius: 'md',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: { xs: 1.5, sm: 1.5, md: 2 }, px: { xs: 1.5, sm: 1.5, md: 2 } }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      {stage}
                    </Typography>
                    {(totalCount > 0) && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
                        {completedCount}/{totalCount} tasks completed
                      </Typography>
                    )}
                  </Box>
                    
                  {(totalCount > 0) && (
                    <Sheet
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 },
                        borderRadius: 'sm',
                        bgcolor: 'background.level1',
                      }}
                    >
                      <Box component="table" sx={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        tableLayout: 'auto',
                        '& td': {
                          p: { xs: 0.25, sm: 0.5 },
                          px: { xs: 0.25, sm: 0.5 },
                          verticalAlign: 'top',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                        },
                        '& td:first-of-type': {
                          width: { xs: 'auto', sm: '40px' },
                          minWidth: { xs: 'auto', sm: '40px' },
                          px: { xs: 0.25, sm: 0.5 },
                        },
                        '& td:last-of-type': {
                          width: { xs: '100%', sm: 'auto' },
                          maxWidth: { xs: '100%', sm: 'none' },
                          px: { xs: 0.25, sm: 0.5 },
                        }
                      }}>
                        <tbody>
                          {stageTasks.map((task: WorkflowTask) => {
                            // Find related event for this task
                            const relatedEventType = TASK_EVENT_TYPE_MAP[task.task];
                            const relatedEvent = sortedEvents.find(e => e.event_type === relatedEventType);
                            const eventDate = relatedEvent?.event_date ? new Date(relatedEvent.event_date).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : null;
                            const isCompleteProject = task.task === 'Complete Project';
                            return (
                              <tr key={task.task}>
                                <td>
                                  <Checkbox
                                    disabled={readOnly}
                                    checked={task.completed}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      onTaskChange(task.task, e.target.checked);
                                    }}
                                    size={condensed ? 'sm' : 'md'}
                                  />
                                </td>
                                <td>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography
                                      component="span"
                                      sx={isCompleteProject && task.completed ? { color: 'neutral.500' } : {}}
                                    >
                                      {task.task}
                                      {task.task === 'Production Date' && task.productionDates && task.productionDates.length > 0 && (
                                        <Chip 
                                          size="sm" 
                                          color="primary" 
                                          sx={{ 
                                            ml: { xs: 0.5, sm: 1 },
                                            mt: { xs: 0.5, sm: 0 },
                                            display: 'inline-block'
                                          }}
                                        >
                                          {task.productionDates.length} date{task.productionDates.length !== 1 ? 's' : ''} set
                                        </Chip>
                                      )}
                                      {eventDate && (
                                        <Chip 
                                          size="sm" 
                                          color="primary" 
                                          sx={{ 
                                            ml: { xs: 0.5, sm: 1 },
                                            mt: { xs: 0.5, sm: 0 },
                                            display: 'inline-block'
                                          }}
                                        >
                                          {eventDate}
                                        </Chip>
                                      )}
                                    </Typography>
                                    {task.task === 'Production Date' && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                                        {readOnly && task.completed && task.productionDates && task.productionDates.length > 0 && !editingProductionDates ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', mt: 1, width: '100%' }}>
                                            {task.productionDates.map((entry) => (
                                              <Typography
                                                key={entry.id}
                                                level="body-sm"
                                                sx={{
                                                  background: entry.productType === 'Glass' ? 'rgba(0, 128, 0, 0.08)' : 'rgba(0, 128, 255, 0.08)',
                                                  color: entry.productType === 'Glass' ? 'green' : '#1976d2',
                                                  borderRadius: 'sm',
                                                  px: 1.5,
                                                  py: 0.5,
                                                  fontWeight: 500,
                                                  display: 'inline-block',
                                                  wordBreak: 'break-word',
                                                  whiteSpace: 'normal',
                                                  maxWidth: '100%',
                                                }}
                                              >
                                                {entry.productType} - {getSupplierName(entry.supplierId)} - {entry.date ? new Date(entry.date).toLocaleDateString('en-GB') : 'No date'}{entry.paid ? ` - ${PRODUCTION_PAID_LABELS[entry.paid]}` : ''}
                                              </Typography>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '100%', overflow: 'visible', mt: 1 }}>
                                            <ProductionDateManager
                                              productionDates={task.productionDates || []}
                                              onProductionDatesChange={(dates) => handleProductionDatesChange(task.task, dates)}
                                              productTypes={project?.product_types}
                                              readOnly={readOnly}
                                            />
                                            {editingProductionDates && (
                                              <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: 1,
                                                width: { xs: '100%', sm: 'auto' }
                                              }}>
                                                <Button
                                                  size="sm"
                                                  variant="solid"
                                                  color="primary"
                                                  onClick={() => setEditingProductionDates(false)}
                                                  sx={{
                                                    width: { xs: '100%', sm: 'auto' }
                                                  }}
                                                >
                                                  Done
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outlined"
                                                  color="neutral"
                                                  onClick={() => setEditingProductionDates(false)}
                                                  sx={{
                                                    width: { xs: '100%', sm: 'auto' }
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                              </Box>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Box>
                    </Sheet>
                  )}
                </Sheet>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Supply Only Toggle Warning Modal */}
      <Modal open={showToggleWarning} onClose={cancelSupplyOnlyChange}>
        <ModalDialog>
          <DialogTitle>Change Workflow Mode</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to change the workflow mode to {pendingSupplyOnlyChange ? 'Supply Only' : 'Standard'}? 
              This will reset all workflow tasks to match the new mode.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="neutral" onClick={cancelSupplyOnlyChange}>
              Cancel
            </Button>
            <Button color="primary" onClick={confirmSupplyOnlyChange}>
              Confirm
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default React.memo(ProjectWorkflow);

export { getStageFromTasks }; 